export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = 'ERROR',
    public readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}

export const AUTH_EXPIRED_EVENT = 'auth:expired';

let refreshing: Promise<boolean> | null = null;

/** Renova o access token usando o refresh cookie (uma única chamada concorrente). */
function refreshSession(): Promise<boolean> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      setTimeout(() => (refreshing = null), 0);
    });
  return refreshing;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retry = true): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    // A API usa cookies HttpOnly. `include` também preserva a sessão se frontend e API
    // forem servidos por origens diferentes numa futura implantação.
    credentials: 'include',
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(`/api${path}`, init);
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError('Keine Verbindung zum Server. Läuft das Backend?', 0, 'NETWORK');
  }

  if (res.status === 401 && retry && !path.startsWith('/auth/login')) {
    const canRefresh = path === '/auth/me' || !path.startsWith('/auth/');
    if (canRefresh && (await refreshSession())) return api<T>(path, opts, false);
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; code?: string; issues?: { path: string; message: string }[] };
    const fallback = res.status >= 500 ? 'Der Server ist gerade nicht erreichbar.' : 'Anfrage fehlgeschlagen.';
    throw new ApiError(d.error ?? fallback, res.status, d.code, d.issues);
  }
  return data as T;
}

export const errorMessage = (err: unknown) =>
  err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Unbekannter Fehler';
